import { EVENT_USER_INFORMATION } from '@modules/app'
import { buildHashTable } from '@modules/cart/utils'
import { Address, Order } from '@modules/commercetools'
import { GaEventsCollection, GaItemParams } from '@oriuminc/base'
import { pushPageViewToDataLayer, UserStats } from '@spoonflower/user-analytics'
import { StatsDataLayer } from '../../../app/hooks/useUserStats'
import {
  CartFragment,
  CartLineItemFragment,
  Maybe,
  ProductFragment,
} from '../types/commercetools-schema'
import {
  GaApplyCouponEvent,
  GaCartFragment,
  GaRemoveCouponEvent,
  GaReviewOrderEvent,
  GaUpdateLocationEvent,
} from '../types/gaTracking'
import { getCartTotals } from '../utils'
import { Discount } from '../utils/checkout/helpers/types'
import {
  concatDiscountCodesWithSpace,
  filterGaItems,
  formatPrice,
  getItemsWithValues,
  MURAL_CATEGORY,
} from './helpers'

const safeWindow = typeof window !== 'undefined' ? window : null

const PURCHASE_EVENT_TIMEOUT = 2000

/**
 * Find an CartLineItemFragment by id and variantId if provided (variantId is necessary to ensure the correct item is found when a product has multiple variants in the cart)
 * @param cartItem
 * @param id
 * @param variantId
 */
const findCartItemByProductIds = (
  cartItem: CartLineItemFragment,
  id: string,
  variantId?: number
) => {
  if (cartItem.productId !== id) {
    return false
  }

  return typeof variantId === 'number'
    ? cartItem.variant?.id === variantId
    : true
}

/**
 * Find an CartLineItemFragment by line item id
 * @param cartItem
 * @param id
 */
const findCartItemByLineItemId = (
  cartItem: CartLineItemFragment,
  id: string
) => {
  return cartItem.id === id
}

export interface ExtendedGaItemParams extends GaItemParams {
  design_id?: string
  item_brand?: string
}

const buildGaItemParams = (
  item: CartLineItemFragment,
  quantity: number
): ExtendedGaItemParams & {
  item_id: string
  item_category: string
  item_name: string
  currency: string
  price: number
  quantity: number
} => {
  const customFields = buildHashTable(item?.custom?.customFieldsRaw)
  const muralId = customFields?.muralId ?? ''

  const fullyQualifiedSku = `${item.variant?.sku}${customFields?.designID ? `-D${customFields.designID}` : ''}`

  return {
    item_id: item.variant?.sku ? fullyQualifiedSku : '',
    item_category: muralId ? MURAL_CATEGORY : (item.productType?.name ?? ''),
    item_name: item.name ?? '',
    item_brand: customFields?.artistName ?? '',
    design_id: customFields?.designID ?? '',
    currency:
      item.price?.value.currencyCode ?? item.totalPrice?.currencyCode ?? '',
    price: formatPrice(
      item.price?.value.centAmount ?? item.totalPrice?.centAmount
    ),
    quantity,
  }
}

/**
 * Tracks a cartItem update
 */
export const gaTrackUpdateCart = (params: {
  lineItemId: string
  quantity?: number
  prevCart?: Maybe<GaCartFragment>
  nextCart?: GaCartFragment
}) => {
  const { prevCart, lineItemId } = params
  const item = prevCart?.lineItems?.find((el) =>
    findCartItemByLineItemId(el, lineItemId)
  )
  const nextQty = params?.quantity ?? 0
  const prevQty = item?.quantity ?? 0
  const quantity = Math.abs(nextQty - prevQty)

  if (!item || quantity <= 0) {
    return
  }

  if (nextQty > prevQty) {
    gaTrackAddToCart({
      ...params,
      productId: item?.productId!,
      variantId: item?.variant?.id!,
      quantity,
    })
  } else {
    gaTrackRemoveFromCart({ ...params, quantity })
  }
}

/**
 * Tracks the add to cart event
 */
export const gaTrackAddToCart = (params: {
  productId: string
  variantId?: number
  quantity?: number
  prevCart?: Maybe<GaCartFragment>
  nextCart?: GaCartFragment
}) => {
  const { nextCart, quantity = 0, productId, variantId } = params
  const item = nextCart?.lineItems?.find((el) =>
    findCartItemByProductIds(el, productId, variantId)
  )

  if (!item || quantity <= 0) {
    return
  }

  const items = filterGaItems([buildGaItemParams(item, quantity)])
  const unitPrice = item.price?.value.centAmount ?? 0
  const currency =
    item.price?.value.currencyCode ?? item.totalPrice?.currencyCode ?? ''

  gaTrackEventPush({
    name: 'add_to_cart',
    params: {
      currency,
      value: formatPrice(unitPrice * quantity),
      add: {
        items,
      },
    },
  })
}

/**
 * Tracks the remove from cart event
 */
export const gaTrackRemoveFromCart = (params: {
  lineItemId: string
  quantity?: number
  prevCart?: Maybe<GaCartFragment>
}) => {
  const { prevCart, quantity, lineItemId } = params
  const item = prevCart?.lineItems?.find((el) =>
    findCartItemByLineItemId(el, lineItemId)
  )

  if (!item) {
    return
  }

  const quantityToSend = quantity ?? item.quantity ?? 0
  if (quantityToSend <= 0) {
    return
  }

  const items = filterGaItems([buildGaItemParams(item, quantityToSend)])
  const unitPrice = item.price?.value.centAmount ?? 0
  const currency =
    item.price?.value.currencyCode ?? item.totalPrice?.currencyCode ?? ''

  gaTrackEventPush({
    name: 'remove_from_cart',
    params: {
      currency,
      value: formatPrice(unitPrice * quantityToSend),
      remove: {
        items,
      },
    },
  })
}

export const gaTrackRemoveItemsFromCart = (params: {
  lineItemIds: string[]
  prevCart?: Maybe<GaCartFragment>
}) => {
  const { prevCart, lineItemIds } = params
  if (!prevCart || !lineItemIds?.length) {
    return
  }

  const lineItemIdSet = new Set(lineItemIds)
  const removedLineItems =
    prevCart.lineItems?.filter((lineItem) => lineItemIdSet.has(lineItem.id)) ??
    []

  if (!removedLineItems.length) {
    return
  }

  const items = getItemsWithValues(removedLineItems)
  if (!items.length) {
    return
  }

  const value = items.reduce(
    (total, item) => total + (item.price ?? 0) * (item.quantity ?? 0),
    0
  )
  const currency =
    removedLineItems[0]?.price?.value.currencyCode ??
    removedLineItems[0]?.totalPrice?.currencyCode ??
    prevCart.totalPrice?.currencyCode ??
    ''

  gaTrackEventPush({
    name: 'remove_from_cart',
    params: {
      currency,
      value,
      remove: {
        items,
      },
    },
  })
}

export const gaTrackViewCart = (params: {
  cart?: GaCartFragment
  discounts?: Discount[]
}) => {
  const { cart, discounts } = params
  if (!cart) {
    return
  }

  const items = getItemsWithValues(cart.lineItems)
  const { total, shipping, discount } = getCartTotals(cart as CartFragment)

  gaTrackEventPush({
    name: 'view_cart',
    params: {
      value: formatPrice(total - shipping),
      currency: cart.totalPrice?.currencyCode ?? '',
      cart_id: cart.id,
      step: 0,
      coupon: concatDiscountCodesWithSpace(discounts ?? []),
      discount: formatPrice(discount),
      items,
    },
  })
}

export const gaTrackBeginCheckout = (params: {
  cart?: GaCartFragment
  discounts?: Discount[]
  checkoutType: string
}) => {
  const { cart, discounts, checkoutType } = params
  if (!cart) {
    return
  }

  const items = getItemsWithValues(cart.lineItems)
  const { total, shipping } = getCartTotals(cart)
  const { discount } = getCartTotals(cart as CartFragment)

  gaTrackEventPush({
    name: 'begin_checkout',
    params: {
      value: formatPrice(total - shipping),
      currency: cart.totalPrice?.currencyCode ?? '',
      coupon: concatDiscountCodesWithSpace(discounts ?? []),
      items,
      discount: formatPrice(discount),
      checkout_type: checkoutType,
    },
  })
}

export const gaTrackAddShippingInfo = (params: {
  cart?: GaCartFragment
  discounts?: Discount[]
  shippingTier?: string
  checkoutType: string
}) => {
  const { cart, discounts, shippingTier, checkoutType } = params
  if (!cart) {
    return
  }

  const items = getItemsWithValues(cart.lineItems)
  const { total, discount, shipping } = getCartTotals(cart)

  gaTrackEventPush({
    name: 'add_shipping_info',
    params: {
      cart_id: cart.id,
      step: 2,
      value: formatPrice(total),
      currency: cart.totalPrice?.currencyCode ?? '',
      coupon: concatDiscountCodesWithSpace(discounts ?? []),
      shipping_tier: shippingTier,
      shipping: formatPrice(shipping),
      discount: formatPrice(discount),
      items,
      checkout_type: checkoutType,
    },
  })
}

export const gaTrackAddPaymentInfo = (params: {
  cart?: GaCartFragment
  discounts?: Discount[]
  paymentType?: string
  shippingTier?: string
  checkoutType: string
}) => {
  const { cart, discounts, paymentType, shippingTier, checkoutType } = params
  if (!cart) {
    return
  }

  const items = getItemsWithValues(cart.lineItems)
  const { total, discount, shipping, tax } = getCartTotals(cart)

  gaTrackEventPush({
    name: 'add_payment_info',
    params: {
      cart_id: cart.id,
      step: 3,
      value: formatPrice(total),
      currency: cart.totalPrice?.currencyCode ?? '',
      coupon: concatDiscountCodesWithSpace(discounts ?? []),
      payment_type: paymentType ?? '',
      shipping_tier: shippingTier,
      shipping: formatPrice(shipping),
      tax: formatPrice(tax),
      discount: formatPrice(discount),
      items,
      checkout_type: checkoutType,
    },
  })
}

// copied from gaTrackAddPaymentInfo above
export const gaTrackReviewOrder = (params: {
  cart?: GaCartFragment
  discounts?: Discount[]
  shippingTier?: string
  paymentType?: string
  checkoutType: string
}) => {
  const { cart, discounts, shippingTier, checkoutType } = params // passing in transactionId as optional param for starter kit requirement
  if (!cart) {
    return
  }

  const items = getItemsWithValues(cart.lineItems)
  const { total, discount, shipping, tax } = getCartTotals(cart)

  gaTrackEventPush({
    name: 'review_order',
    params: {
      cart_id: cart.id,
      step: 4,
      currency: cart.totalPrice?.currencyCode ?? '',
      value: formatPrice(total),
      coupon: concatDiscountCodesWithSpace(discounts ?? []),
      payment_type: params.paymentType ?? '',
      shipping_tier: shippingTier,
      shipping: formatPrice(shipping),
      tax: formatPrice(tax),
      discount: formatPrice(discount),
      items,
      checkout_type: checkoutType,
    },
  })
}

export const gaTrackPurchase = (params: {
  order?: Order | undefined
  discounts?: Discount[]
  transactionId: string
  paymentType?: string
  checkoutType: string
}): Promise<void> => {
  const { order, discounts, transactionId, checkoutType } = params
  if (!order) {
    return Promise.resolve()
  }
  const items = getItemsWithValues(order.lineItems)
  const { total, discount, tax, shipping } = getCartTotals({
    shippingInfo: order.shippingInfo,
    taxedPrice: order.taxedPrice,
    lineItems: order.lineItems as Partial<Order['lineItems']> as Partial<
      CartFragment['lineItems']
    >,
  } as CartFragment)

  return new Promise<void>((resolve) => {
    const timeout = setTimeout(resolve, PURCHASE_EVENT_TIMEOUT)
    const done = () => {
      clearTimeout(timeout)
      resolve()
    }

    try {
      gaTrackClearEcommerce()
      safeWindow?.dataLayer?.push({
        event: 'purchase',
        ecommerce: {
          currency: order.totalPrice?.currencyCode ?? '',
          transaction_id: transactionId,
          shipping: formatPrice(shipping),
          shipping_tier: order.shippingInfo?.shippingMethodName ?? '',
          cart_id: order.cart?.id,
          step: 5,
          value: formatPrice(total),
          coupon: concatDiscountCodesWithSpace(discounts ?? []),
          payment_type: params.paymentType ?? '',
          checkout_type: checkoutType,
          tax: formatPrice(tax),
          discount: formatPrice(discount),
          items,
        },
        eventCallback: done,
        eventTimeout: PURCHASE_EVENT_TIMEOUT,
      })
      // If GTM is not loaded (e.g. blocked by an ad blocker), dataLayer.push
      // still returns a number so checking `pushed === undefined` never works.
      // Instead, detect GTM's own presence via window.google_tag_manager which
      // GTM sets on itself when it initialises — if absent, resolve immediately.
      if (!safeWindow?.google_tag_manager) {
        done()
      }
    } catch {
      done()
    }
  })
}

export const gaTrackViewItem = (params: {
  product?: Partial<ProductFragment>
}) => {
  const { product } = params
  if (!product) {
    return
  }

  const price = product.masterVariant?.price

  // TODO: fix ts-ignore. Price types are not coming through
  gaTrackEventPush({
    name: 'view_item',
    params: {
      // @ts-ignore
      value: formatPrice(price?.value.centAmount),
      // @ts-ignore
      currency: price?.value.currencyCode ?? '',
      items: [
        {
          item_id: product?.id ?? '',
          item_name: product?.name ?? '',
          // @ts-ignore
          currency: price?.value.currencyCode ?? '',
          // @ts-ignore
          price: formatPrice(price?.value.centAmount),
          quantity: 1,
        },
      ],
    },
  })
}

export const gaTrackApplyCoupon = (params: {
  cart?: GaCartFragment
  couponCode?: string
}) => {
  const { cart, couponCode } = params
  if (!cart || !couponCode) {
    return
  }

  gaTrackEventPush({
    name: 'apply_coupon',
    params: {
      coupon: couponCode,
      currency: cart.totalPrice?.currencyCode ?? '',
      value: formatPrice(cart.totalPrice?.centAmount).toString(),
    },
  })
}

export const gaTrackRemoveCoupon = (params: { cart?: GaCartFragment }) => {
  const { cart } = params
  if (!cart) {
    return
  }

  gaTrackEventPush({
    name: 'remove_coupon',
    params: {
      currency: cart.totalPrice?.currencyCode ?? '',
      value: formatPrice(cart.totalPrice?.centAmount).toString(),
    },
  })
}

export const gaTrackUpdateLocation = (params: {
  currencyCode?: string
  countryCode?: string
}) => {
  const { currencyCode, countryCode } = params

  if (countryCode) {
    gaTrackEventPush({
      name: 'update_location',
      params: {
        country: countryCode,
      },
    })
  } else {
    gaTrackEventPush({
      name: 'update_location',
      params: {
        currency: currencyCode,
      },
    })
  }
}

type GaEventExtended =
  | GaEventsCollection
  | GaReviewOrderEvent
  | GaApplyCouponEvent
  | GaRemoveCouponEvent
  | GaUpdateLocationEvent

export const gaTrackEventPush = (event: any) => {
  // TODO: Fix type
  gaTrackClearEcommerce()
  safeWindow?.dataLayer?.push({ event: event.name, ecommerce: event.params })
}

export const gaTrackUserInformation = (
  pageType: string,
  properties: StatsDataLayer
) => {
  safeWindow?.dataLayer?.push({
    ...properties,
    event: EVENT_USER_INFORMATION,
    pageType: pageType,
  })
}

export const gaTrackShippingInfo = (
  shippingAddress: Pick<
    Address,
    'firstName' | 'lastName' | 'postalCode' | 'state' | 'city' | 'phone'
  >
) => {
  safeWindow?.dataLayer?.push({
    event: 'shipping_info',
    firstName: shippingAddress.firstName ?? '',
    lastName: shippingAddress.lastName ?? '',
    zipCode: shippingAddress.postalCode ?? '',
    shippingState: shippingAddress.state ?? '',
    shippingCity: shippingAddress.city ?? '',
    phoneNumber: shippingAddress.phone ?? '',
  })
}

export const gaTrackPageType = (pageType: string) => {
  safeWindow?.dataLayer?.push({
    event: 'page-type',
    pageType: pageType,
  })
}

export type SplitTest = {
  id: `experiment_${string}`
  group: string
}

export const gaTrackPageView = (
  pageType: string,
  userStats: UserStats['data_layer'],
  cartId: string,
  guestCartId: string,
  exposures: SplitTest[]
) => {
  pushPageViewToDataLayer({
    pageType,
    userStats,
    cartId,
    guestCartId,
    splitTesting: { exposures },
  })
}

export const gaTrackClearEcommerce = () => {
  safeWindow?.dataLayer?.push({
    event: 'clear-ecommerce',
    ecommerce: null,
  })
}

export const gaTrackExposure = (exposures: SplitTest[]) => {
  safeWindow?.dataLayer?.push({
    event: 'split_testing',
    split_testing: { exposures },
  })
}
